import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { Dispatch } from 'redux';
import AppState from '@webapp/store/state';
import {
  removeLocalStorageItem,
  getLocalStorageItem,
} from '@webapp/utils/localstorage';
import { getStripeCheckoutSession } from '@webapp/sdk/payment';
import { confirmMerchOrder } from '@webapp/sdk/merch';
import { orderCompletedMerchEvent } from '@webapp/utils/rudderstack/parseEvents';
import MotionOpacity from '@components/motion/motion-opacity';
import { Flex } from '@components/layout/flex';
import SimpleArtist from '@common/types/artist/simple-artist';
import { Translate } from 'react-localize-redux';

interface MerchOrderConfirmationPageProps {
  className?: string;
  artist?: SimpleArtist;
  goTo: (url: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  artist: state.artist.active,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goTo: (url: string) => dispatch(push(url)),
});

const PaymentConfirmation = (props: MerchOrderConfirmationPageProps) => {
  const order = getLocalStorageItem('merchSampleOrder');

  React.useEffect(() => {
    getStripeCheckoutSession(
      getLocalStorageItem('stripeCheckoutSessionId')
    ).then((data) => {
      if (data.status === 'complete') {
        confirmMerchOrder(
          getLocalStorageItem('orderId'),
          data.amount_total,
          data.amount_subtotal,
          data.total_details?.amount_shipping,
          data.total_details?.amount_tax,
          data.currency,
          data.id
        ).then(() => {
          orderCompletedMerchEvent({
            order,
            artist: props.artist,
            total: data.amount_total || undefined,
          });

          removeLocalStorageItem('stripeCheckoutSessionId');
          removeLocalStorageItem('merchSampleOrder');

          const toMainTimer = setTimeout(() => {
            props.goTo(
              `/catalog?type=merch&productId=${order.products[0].id}&orderId=${order.id}`
            );
          }, 3000);

          return () => {
            clearTimeout(toMainTimer);
          };
        });
      }
    });
  }, [order, props]);

  return (
    <Container>
      <ConfirmationWrapper className={props.className}>
        <Title>
          <Translate id="MERCH.PRODUCT_SAMPLE.ORDER_CONFIRMATION.TITLE" />
        </Title>
        <Description className="has-marign">
          <Translate id="MERCH.PRODUCT_SAMPLE.ORDER_CONFIRMATION.DETAILS" />
        </Description>
      </ConfirmationWrapper>
      <RedirectingContainer>
        <p className={'redirecting'}>
          <Translate id="MERCH.PRODUCT_SAMPLE.ORDER_CONFIRMATION.REDIRECT_TITLE" />
        </p>
        <a
          href={`/catalog?type=merch&productId=${order?.products[0].id}&orderId=${order?.id}`}
        >
          <Translate id="MERCH.PRODUCT_SAMPLE.ORDER_CONFIRMATION.REDIRECT_TEXT" />
        </a>
      </RedirectingContainer>
    </Container>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PaymentConfirmation);

const RedirectingContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  color: ${({ theme }) => theme.colors.primary};
  transform: translate(-50%, -50%);
  display: flex;
  max-width: 300px;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  a,
  p {
    font-family: 'Sen', sans-serif;
    font-size: 14px;
    line-height: 18px;
    text-decoration: underline;
  }

  .redirecting {
    text-transform: uppercase;
    font-weight: 700;
    max-width: 180px;
    text-decoration: none;
  }

  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.tablet}px)`} {
    top: 44%;
    max-width: 400px;
  }

  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.laptopS}px)`} {
    top: 80%;
  }
`;

const Container = styled(MotionOpacity)`
  position: relative;
  margin-top: 100px;
`;

const ConfirmationWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 340px;
  height: 220px;
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${({ theme }) => theme.colors.white};
  padding: 80px 30px 20px;
  border-radius: 4px;

  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.tablet}px)`} {
    max-width: 420px;
    top: 30%;
  }

  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.laptopS}px)`} {
    top: 50%;
  }
`;

const Title = styled.h4`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 24px;
  font-weight: 400;
  line-height: 30px;
  margin: 0;
  text-align: left;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  margin: 12px 0 0;
  opacity: 0.5;
  text-align: left;

  &.has-marign {
    margin-top: 12px;
  }
`;
